import { Link } from "react-router-dom";
import {
  MessageCircle, Megaphone, Calendar, BarChart3, Users, CreditCard,
  MessagesSquare, ShieldCheck, ArrowRight,
} from "lucide-react";

const FEATURES = [
  { icon: MessageCircle, title: "Group Chat", desc: "Real-time channels to talk ball, reconnect, and stay in the loop with fellow alumni." },
  { icon: MessagesSquare, title: "Direct Messages", desc: "Private 1-on-1 messaging with likes and replies to reach any member directly." },
  { icon: Megaphone, title: "Announcements", desc: "Official CFAA news and updates, pinned so you never miss what matters." },
  { icon: Calendar, title: "Events & RSVPs", desc: "Reunions, games, and gatherings with photos, details, and one-tap RSVP." },
  { icon: BarChart3, title: "Polls", desc: "Vote on team decisions, event dates, and community questions." },
  { icon: Users, title: "Members Directory", desc: "Find and connect with alumni by name and the years they played." },
  { icon: CreditCard, title: "Membership & Dues", desc: "Join, pay dues, and manage your membership securely with Stripe." },
  { icon: ShieldCheck, title: "Private & Secure", desc: "Role-based access keeps the community members-only and your data protected." },
];

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white/95 border border-white/40 p-5 shadow-sm" data-testid={`welcome-feature-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="w-11 h-11 rounded-xl bg-purple-700 text-white flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-3 text-base font-black font-display text-slate-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Welcome() {
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "linear-gradient(rgba(30,10,60,0.86), rgba(20,8,45,0.92)), url(/umhb-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="mx-auto max-w-5xl px-6 py-14">
        {/* Hero */}
        <div className="text-center">
          <img src="/cfaa-mark.png" alt="Crusader Football Alumni Association" data-testid="welcome-logo" className="mx-auto w-64 sm:w-80 h-auto rounded-2xl shadow-2xl" />
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-black font-display tracking-tight text-white">
            Crusader Football Alumni Association
          </h1>
          <p className="mt-4 text-base md:text-lg text-purple-100 max-w-2xl mx-auto">
            The private home for Crusader football alumni — stay connected, relive the tradition,
            and keep the brotherhood strong. 3X National Champions: 2016 &bull; 2018 &bull; 2021.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/login"
              data-testid="welcome-join-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-sm btn-lift"
            >
              Sign in or Join <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/privacy"
              data-testid="welcome-privacy-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/30"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              data-testid="welcome-terms-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/30"
            >
              Terms of Service
            </Link>
          </div>
        </div>

        {/* What's inside */}
        <div className="mt-16">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-amber-300 font-bold">What's inside the app</p>
          <h2 className="text-center text-base md:text-lg text-white/90 font-semibold mt-2">
            Everything alumni need in one members-only community
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => <Feature key={f.title} {...f} />)}
          </div>
        </div>

        {/* Reviewer note */}
        <div className="mt-14 rounded-2xl bg-white/10 border border-white/20 p-6 text-center">
          <p className="text-sm text-purple-100 leading-relaxed max-w-2xl mx-auto">
            CFAA is a private, members-only community. New members create an account and are approved by an
            administrator. Membership dues are handled securely through Stripe. Access to features is based on
            each member's role and standing.
          </p>
        </div>

        <p className="mt-10 text-center text-xs text-purple-200/70">
          &copy; {new Date().getFullYear()} Crusader Football Alumni Association &middot;{" "}
          <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> &middot;{" "}
          <Link to="/terms" data-testid="welcome-terms-link" className="underline hover:text-white">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
